<script setup>
import { useForm } from '@inertiajs/vue3'
import { useToast } from 'primevue/usetoast'
import AppLayout from '@/Layouts/AppLayout.vue'

const toast = useToast()

const props = defineProps({
  profile: Object,
})

const form = useForm({
  firstname: props.profile.firstname,
  lastname: props.profile.lastname,
  email: props.profile.email,
  phone: props.profile.phone,
  profession_level: props.profile.profession_level,
  profession_no: props.profile.profession_no,
  land_appraiser: props.profile.land_appraiser,
  is_member_ekhy: props.profile.is_member_ekhy,
  is_member_ekmk: props.profile.is_member_ekmk,
})

const update = () => {
  form.post(route('profile-update'), {
    onSuccess: () => {
      toast.add({
        severity: 'success',
        summary: 'Õnnestus!',
        detail: 'Profiil uuendatud',
        life: 6000,
      })
    },
    onError: errors => {
      Object.keys(errors).forEach(error => {
        toast.add({
          severity: 'error',
          summary: 'Viga!',
          detail: errors[error],
          life: 6000,
        })
      })
    },
  })
}

const profession_level_options = [
  { label: 'Vara hindaja, tase 7', value: 'Vara hindaja, tase 7' },
  { label: 'Kinnisvara hindaja, tase 6', value: 'Kinnisvara hindaja, tase 6' },
  { label: 'Nooremhindaja, tase 5', value: 'Nooremhindaja, tase 5' },
  { label: 'Hindaja', value: 'Hindaja' },
  { label: 'Kinnisvaramaakler, tase 5', value: 'Kinnisvaramaakler, tase 5' },
  { label: 'Kinnisvara vanemmaakler, tase 6', value: 'Kinnisvara vanemmaakler, tase 6' },
  { label: 'Maakler', value: 'Maakler' },
  { label: '-', value: '-' },
]
</script>

<template>
  <AppLayout>
    <div class="flex flex-col w-full">
      <div class="pricing-header px-3 py-6 pt-md-5 pb-md-4 mx-auto text-center">
        <h1 class="text-2xl font-bold">{{ $t('messages.profile') }}</h1>
      </div>

      <div class="flex justify-center">
        <Card class="max-w-md w-full">
          <template #header>
            <div class="flex bg-gray-100 p-4 rounded-t-md">
              <h5>
                {{ $t('messages.profile') }}
              </h5>
            </div>
          </template>

          <template #content>
            <form @submit.prevent="update" class="flex flex-col gap-2">
              <div>
                <label for="firstname">
                  {{ $t('messages.FirstName') }}
                </label>

                <InputText
                  id="firstname"
                  type="text"
                  name="firstname"
                  v-model="form.firstname"
                  :invalid="!!form.errors.firstname"
                  required
                  autofocus
                  fluid
                />

                <span v-if="form.errors.firstname" class="mt-1 text-sm text-red-600" role="alert">
                  <strong>{{ form.errors.firstname }}</strong>
                </span>
              </div>

              <div>
                <label for="lastname">
                  {{ $t('messages.LastName') }}
                </label>

                <InputText
                  id="lastname"
                  type="text"
                  name="lastname"
                  v-model="form.lastname"
                  :invalid="!!form.errors.lastname"
                  required
                  autofocus
                  fluid
                />

                <span v-if="form.errors.lastname" class="mt-1 text-sm text-red-600" role="alert">
                  <strong>{{ form.errors.lastname }}</strong>
                </span>
              </div>

              <div>
                <label for="email">
                  {{ $t('messages.Email') }}
                </label>

                <InputText
                  id="email"
                  type="email"
                  name="email"
                  v-model="form.email"
                  :invalid="!!form.errors.email"
                  required
                  fluid
                />

                <span v-if="form.errors.email" class="mt-1 text-sm text-red-600" role="alert">
                  <strong>{{ form.errors.email }}</strong>
                </span>
              </div>

              <div>
                <label for="phone">
                  {{ $t('messages.phone') }}
                </label>

                <InputText
                  id="phone"
                  type="phone"
                  name="phone"
                  v-model="form.phone"
                  :invalid="!!form.errors.phone"
                  required
                  fluid
                />

                <span v-if="form.errors.phone" class="mt-1 text-sm text-red-600" role="alert">
                  <strong>{{ form.errors.phone }}</strong>
                </span>
              </div>

              <div>
                <label for="profession_level">
                  {{ $t('messages.professionLevel') }}
                </label>

                <Select
                  id="profession_level"
                  name="profession_level"
                  :options="profession_level_options"
                  v-model="form.profession_level"
                  option-label="label"
                  option-value="value"
                  :invalid="!!form.errors.profession_level"
                  required
                  fluid
                />

                <span
                  v-if="form.errors.profession_level"
                  class="mt-1 text-sm text-red-600"
                  role="alert"
                >
                  <strong>{{ form.errors.profession_level }}</strong>
                </span>
              </div>

              <div>
                <label for="profession_no">
                  {{ $t('messages.professionNo') }}
                </label>

                <InputText
                  id="profession_no"
                  type="text"
                  name="profession_no"
                  v-model="form.profession_no"
                  :invalid="!!form.errors.profession_no"
                  fluid
                />

                <span
                  v-if="form.errors.profession_no"
                  class="mt-1 text-sm text-red-600"
                  role="alert"
                >
                  <strong>{{ form.errors.profession_no }}</strong>
                </span>
              </div>

              <div class="flex items-center gap-2">
                <label for="is_member_ekhy">
                  {{ $t('messages.isMember.ekhy') }}
                </label>

                <div>
                  <Checkbox name="is_member_ekhy" v-model="form.is_member_ekhy" binary />
                </div>
              </div>

              <div class="flex items-center gap-2">
                <label for="is_member_ekmk">
                  {{ $t('messages.isMember.ekmk') }}
                </label>

                <div>
                  <Checkbox name="is_member_ekmk" v-model="form.is_member_ekmk" binary />
                </div>
              </div>

              <div class="w-full flex justify-end mt-2">
                <Button type="submit" variant="primary">
                  {{ $t('messages.update') }}
                </Button>
              </div>
            </form>
          </template>
        </Card>
      </div>
    </div>
  </AppLayout>
</template>
